import { SettingId, SettingRaw, UserSettingGetAndMigrate } from '@eih-netbroker/common';
import {
  UserSetting,
  UserSettingsAccount,
  UserSettingsChartDrawing,
  UserSettingsChartLayout,
  UserSettingsContainerSize,
  UserSettingsFavoriteLayouts,
  UserSettingsFavoriteWidgets,
  UserSettingsGridColumns,
  UserSettingsGridColumnsWidth,
  UserSettingsLastSelectedAccount,
  UserSettingsLayout2,
  UserSettingsLayoutDisplay,
  UserSettingsMarketTipState,
  UserSettingsNotifications,
  UserSettingsSelectedWidgets,
  UserSettingsUserInterface,
  UserSettingsUserMoneyVisible,
  UserSettingsWatchListState,
  UserSettingsWidgetContainer,
  UserSettingWatchList,
} from '../state/user-settings/userSettings';

export const userSettingMigrationMap: { [key in UserSetting]?: UserSettingGetAndMigrate<key, unknown> } = {
  [UserSetting.Account]: UserSettingsAccount,
  [UserSetting.ChartDrawing]: UserSettingsChartDrawing,
  [UserSetting.ChartLayout]: UserSettingsChartLayout,
  [UserSetting.Notifications]: UserSettingsNotifications,
  [UserSetting.UserInterface]: UserSettingsUserInterface,
  [UserSetting.WatchList]: UserSettingWatchList,
  [UserSetting.WidgetContainerState]: UserSettingsWidgetContainer,
  [UserSetting.GridColumns]: UserSettingsGridColumns,
  [UserSetting.WatchListState]: UserSettingsWatchListState,
  [UserSetting.MarketTipChecked]: UserSettingsMarketTipState,
  [UserSetting.LastSelectedAccount]: UserSettingsLastSelectedAccount,
  [UserSetting.Layout2]: UserSettingsLayout2,
  [UserSetting.LayoutDisplay]: UserSettingsLayoutDisplay,
  [UserSetting.ContainerSizes]: UserSettingsContainerSize,
  [UserSetting.SelectedWidgets]: UserSettingsSelectedWidgets,
  [UserSetting.FavoriteLayouts]: UserSettingsFavoriteLayouts,
  [UserSetting.FavoriteWidgets]: UserSettingsFavoriteWidgets,
  [UserSetting.GridColumnsWidth]: UserSettingsGridColumnsWidth,
  [UserSetting.UserMoneyVisible]: UserSettingsUserMoneyVisible,
};

const getDefault = <T>(migrator: UserSettingGetAndMigrate<UserSetting, T>, settingId: SettingId<UserSetting>) => {
  if (typeof migrator.default === 'function') {
    const settingIdSubId = typeof settingId === 'object' ? settingId.settingId : undefined;
    return (migrator.default as (settingId?: string | number) => T)(settingIdSubId) as T;
  }
  return migrator.default;
};

const getSettingLatestVersion2 = (settingId: SettingId<UserSetting>): number => {
  const setting = typeof settingId === 'object' ? settingId.setting : settingId;
  const migrator = userSettingMigrationMap[setting];
  if (!migrator) {
    throw new Error(`No migrator found for ${setting}`);
  }

  if (!migrator.versions || migrator.versions.length === 0) {
    return 1;
  }

  return Math.max(...migrator.versions?.map(v => v.version));
};

const getAndMigrateUserSetting = <T>(settingId: SettingId<UserSetting>, settings: Array<SettingRaw>): T => {
  const userSetting = typeof settingId === 'object' ? settingId.setting : settingId;
  const key = typeof settingId === 'object' ? `${settingId.setting}/${settingId.settingId.toString()}` : settingId;

  // check if we have a user setting migrator/default value for this user setting
  const migrator = userSettingMigrationMap[userSetting] as UserSettingGetAndMigrate<UserSetting, T> | undefined;
  if (!migrator) {
    throw new Error(`No migrator found for ${userSetting}`);
  }

  // find the latest version of the saved user setting
  const latestSavedVersion = settings
    .filter(s => s.key === key)
    .reduce((acc, item) => {
      // TODO api is wrong, key cannot be undefined!
      if (item.version === undefined) {
        throw new Error();
      }

      const version = item.version;
      if (version > acc) {
        return version;
      }

      return acc;
    }, 0);

  // find the latest saved setting we have for this user setting
  const latestSetting = settings
    .filter(s => s.key === key)
    .find(item => item.version.toString() === latestSavedVersion.toString());

  // if we don't have the latest setting, return the default value
  if (!latestSetting || !migrator.versions || migrator.versions.length === 0) {
    if (latestSetting) {
      return JSON.parse(latestSetting.data) as T;
    } else {
      return getDefault(migrator, settingId);
    }
  }

  // check if the latest saved version is the same as the latest active version
  const latestActiveVersion = migrator.versions.reduce((acc, item) => (item.version > acc ? item.version : acc), 0);
  if (latestSavedVersion === latestActiveVersion) {
    return JSON.parse(latestSetting.data) as T;
  }

  // otherwise try to migrate the setting
  try {
    const migratorsToRun = migrator.versions.filter(item => item.version > latestSavedVersion);
    return migratorsToRun.reduce((acc: unknown, item) => {
      return item.migrate(acc);
    }, JSON.parse(latestSetting.data)) as T;
  } catch (err) {
    return getDefault(migrator, settingId);
  }
};

export const UserSettingsUtils = {
  getDefault,
  getSettingLatestVersion2,
  getAndMigrateUserSetting,
};
